import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  History,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  SWATID_EVENT_TYPES,
  SWATID_EVENT_LABELS,
  SWATID_EVENTS_PAGE_SIZE,
} from '@/constants/swatid';
import {
  useSwatidEvents,
  useSwatidEventsCompare,
  useSwatidRelays,
} from '@/hooks/useSwatidController';
import {
  analyzeEventsCompare,
  describeRelayEventAction,
  filterEventsByDate,
  formatSwatidEventTime,
  getEventDateIso,
  getSwatidEventCategory,
  getSwatidEventLabel,
  isLikelyManualRelayEvent,
} from '@/utils/swatidEvents';
import { getSwatidRelayOptionLabel } from '@/utils/swatidRelay';
import { SwatidScheduleTimeline } from './SwatidScheduleTimeline';
import { cn } from '@/lib/utils';

interface SwatidEventsSectionProps {
  controllerId: string;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

const categoryStyles: Record<string, string> = {
  relay: 'border-blue-500/30 bg-blue-500/5',
  schedule: 'border-amber-500/30 bg-amber-500/5',
  mode: 'border-purple-500/30 bg-purple-500/5',
  other: 'border-border bg-muted/30',
};

export function SwatidEventsSection({ controllerId }: SwatidEventsSectionProps) {
  const [relayFilter, setRelayFilter] = useState<number | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [eventsDate, setEventsDate] = useState<string>('');
  const [eventsPage, setEventsPage] = useState(0);
  const [compareDate, setCompareDate] = useState(todayIsoDate());
  const [compareRelay, setCompareRelay] = useState<number | 'all'>('all');

  useEffect(() => {
    setEventsPage(0);
  }, [relayFilter, typeFilter, eventsDate]);

  const eventParams = useMemo(
    () => ({
      limit: SWATID_EVENTS_PAGE_SIZE,
      offset: eventsPage * SWATID_EVENTS_PAGE_SIZE,
      relay: relayFilter === 'all' ? undefined : relayFilter,
      type: typeFilter === 'all' ? undefined : typeFilter,
      date: eventsDate || undefined,
    }),
    [relayFilter, typeFilter, eventsDate, eventsPage]
  );

  const compareParams = useMemo(
    () => ({
      date: compareDate,
      relay: compareRelay === 'all' ? undefined : compareRelay,
    }),
    [compareDate, compareRelay]
  );

  const hintCompareParams = useMemo(
    () => ({
      date: eventsDate || compareDate,
      relay: relayFilter === 'all' ? undefined : relayFilter,
    }),
    [eventsDate, compareDate, relayFilter]
  );

  const { data: relays = [] } = useSwatidRelays(controllerId, true);
  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useSwatidEvents(
    controllerId,
    eventParams,
    true
  );
  const { data: compareData, isLoading: compareLoading, error: compareError } =
    useSwatidEventsCompare(controllerId, compareParams, true);
  const { data: hintCompareData } = useSwatidEventsCompare(
    controllerId,
    hintCompareParams,
    true
  );

  const displayedEvents = useMemo(() => {
    const events = eventsData?.events ?? [];
    if (!eventsDate) return events;
    return filterEventsByDate(events, eventsDate);
  }, [eventsData?.events, eventsDate]);

  const totalEvents = eventsData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalEvents / SWATID_EVENTS_PAGE_SIZE));
  const pageStart = totalEvents === 0 ? 0 : eventsPage * SWATID_EVENTS_PAGE_SIZE + 1;
  const pageEnd = Math.min((eventsPage + 1) * SWATID_EVENTS_PAGE_SIZE, totalEvents);

  const analysis = useMemo(
    () => (compareData ? analyzeEventsCompare(compareData) : null),
    [compareData]
  );

  const compareSchedulesForManual = hintCompareData?.schedules ?? [];

  return (
    <Tabs defaultValue="events" className="space-y-4">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="events" className="gap-2">
          <History className="w-4 h-4" />
          Historial
        </TabsTrigger>
        <TabsTrigger value="compare" className="gap-2">
          <Activity className="w-4 h-4" />
          vs Programaciones
        </TabsTrigger>
      </TabsList>

      <TabsContent value="events" className="space-y-4 mt-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Día
            </Label>
            <Input
              type="date"
              value={eventsDate}
              onChange={(e) => setEventsDate(e.target.value)}
              className="h-9 w-[160px]"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Relé</Label>
            <select
              value={relayFilter === 'all' ? 'all' : String(relayFilter)}
              onChange={(e) =>
                setRelayFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[140px]"
            >
              <option value="all">Todos</option>
              {relays.map((r) => (
                <option key={r.relayNumber} value={r.relayNumber}>
                  {getSwatidRelayOptionLabel(r)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipo</Label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[180px]"
            >
              <option value="all">Todos</option>
              {SWATID_EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SWATID_EVENT_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          {eventsDate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => setEventsDate('')}
            >
              Quitar día
            </Button>
          )}
          <Badge variant="outline" className="h-9 px-3 flex items-center gap-1">
            <Filter className="w-3 h-3" />
            {totalEvents} eventos
          </Badge>
        </div>

        {eventsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : eventsError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {eventsError instanceof Error ? eventsError.message : 'Error al cargar eventos'}
            </AlertDescription>
          </Alert>
        ) : displayedEvents.length === 0 ? (
          <Alert>
            <AlertDescription>
              Sin eventos registrados con los filtros actuales
              {eventsDate ? ` para el ${eventsDate}` : ''}.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <ul className="space-y-2">
              {displayedEvents.map((event) => {
                const category = getSwatidEventCategory(event.eventType);
                const eventTimeOnly = formatSwatidEventTime(event).split(', ')[1] ?? '';
                const isManualHint =
                  event.relayNumber != null &&
                  event.eventType === 'RELAY_COMMAND_STARTED' &&
                  getEventDateIso(event) === (hintCompareData?.date ?? (eventsDate || compareDate)) &&
                  isLikelyManualRelayEvent(
                    {
                      eventType: event.eventType,
                      relayNumber: event.relayNumber,
                      time: eventTimeOnly.slice(0, 5),
                    },
                    compareSchedulesForManual
                  );

                return (
                  <li
                    key={event.id}
                    className={cn('rounded-lg border p-3 text-sm', categoryStyles[category])}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{getSwatidEventLabel(event.eventType)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatSwatidEventTime(event)}
                          {event.relayNumber != null && ` · Relé ${event.relayNumber}`}
                          {event.eventData &&
                            ` · ${describeRelayEventAction(event.eventType, event.eventData)}`}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">
                          {category === 'relay'
                            ? 'Relé'
                            : category === 'mode'
                              ? 'Modo'
                              : category === 'schedule'
                                ? 'Prog.'
                                : 'Otro'}
                        </Badge>
                        {isManualHint && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-amber-500/50 text-amber-700"
                          >
                            Posible manual
                          </Badge>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {totalEvents > SWATID_EVENTS_PAGE_SIZE && (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Mostrando {pageStart}–{pageEnd} de {totalEvents} · Página {eventsPage + 1} de{' '}
                  {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={eventsPage === 0}
                    onClick={() => setEventsPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Anterior
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={eventsPage >= totalPages - 1}
                    onClick={() => setEventsPage((p) => p + 1)}
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </TabsContent>

      <TabsContent value="compare" className="space-y-4 mt-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Fecha
            </Label>
            <Input
              type="date"
              value={compareDate}
              onChange={(e) => setCompareDate(e.target.value)}
              className="h-9 w-[160px]"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Relé</Label>
            <select
              value={compareRelay === 'all' ? 'all' : String(compareRelay)}
              onChange={(e) =>
                setCompareRelay(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[140px]"
            >
              <option value="all">Todos</option>
              {relays.map((r) => (
                <option key={r.relayNumber} value={r.relayNumber}>
                  {getSwatidRelayOptionLabel(r)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {compareLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : compareError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {compareError instanceof Error ? compareError.message : 'Error en comparación'}
            </AlertDescription>
          </Alert>
        ) : compareData ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Card className="p-3">
                <p className="text-xs text-muted-foreground">Día</p>
                <p className="font-medium capitalize">{compareData.dayName}</p>
                <p className="text-xs">{compareData.date}</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-muted-foreground">Programaciones</p>
                <p className="text-2xl font-semibold">{compareData.schedulesCount}</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-muted-foreground">Eventos relé</p>
                <p className="text-2xl font-semibold">{compareData.eventsCount}</p>
              </Card>
              <Card className="p-3">
                <p className="text-xs text-muted-foreground">Ventanas OK</p>
                <p className="text-2xl font-semibold text-success">
                  {analysis?.matchedWindows ?? 0}
                </p>
              </Card>
            </div>

            {analysis && analysis.mismatches.length > 0 ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">
                    {analysis.mismatches.length} desajuste(s) detectado(s)
                  </p>
                  <ul className="space-y-1 text-sm max-h-[160px] overflow-y-auto">
                    {analysis.mismatches.map((m, i) => (
                      <li key={i}>• {m.message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-success/30 bg-success/5">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <AlertDescription>
                  Sin desajustes detectados entre programaciones y eventos para esta fecha.
                </AlertDescription>
              </Alert>
            )}

            <SwatidScheduleTimeline compareData={compareData} relays={relays} />

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Detalle programaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  {compareData.schedules.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin programaciones</p>
                  ) : (
                    <ul className="space-y-1.5 text-sm max-h-[200px] overflow-y-auto">
                      {compareData.schedules.map((s, i) => (
                        <li key={i} className="flex justify-between rounded bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5">
                          <span>Relé {s.relayNumber}</span>
                          <span className="font-mono text-xs">
                            {s.startTime} – {s.endTime}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Detalle eventos reales</CardTitle>
                </CardHeader>
                <CardContent>
                  {compareData.events.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin eventos de relé</p>
                  ) : (
                    <ul className="space-y-1.5 text-sm max-h-[200px] overflow-y-auto">
                      {compareData.events.map((e, i) => {
                        const manual = isLikelyManualRelayEvent(e, compareData.schedules);
                        return (
                          <li
                            key={i}
                            className={cn(
                              'flex justify-between gap-2 rounded px-2 py-1.5',
                              manual
                                ? 'bg-amber-500/10 border border-amber-500/20'
                                : e.eventType === 'RELAY_COMMAND_STARTED'
                                  ? 'bg-blue-500/10 border border-blue-500/20'
                                  : 'bg-muted/40'
                            )}
                          >
                            <span className="truncate">
                              R{e.relayNumber} · {getSwatidEventLabel(e.eventType)}
                              {manual && (
                                <span className="text-amber-700 text-xs ml-1">(manual?)</span>
                              )}
                            </span>
                            <span className="font-mono text-xs shrink-0">{e.time}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            {analysis && analysis.unscheduledActivations > 0 && (
              <p className="text-xs text-muted-foreground">
                {analysis.unscheduledActivations} activación(es) fuera de ventana programada — pueden
                corresponder a acciones manuales, remotas o intervención de supervisión.
              </p>
            )}
          </>
        ) : null}
      </TabsContent>
    </Tabs>
  );
}
